<template>
  <div v-if="report" class="mex-rpt">
    <div class="mex-rpt-hero">
      <div class="mex-rpt-hero-copy">
        <p class="mex-rpt-kicker">{{ kicker }}</p>
        <div class="mex-rpt-score-row">
          <span class="mex-rpt-score">{{ scoreInt }}</span>
          <span class="mex-rpt-score-unit">分</span>
        </div>
        <p v-if="isPlacement && formalLevelText" class="mex-rpt-level">
          综合等级 <b>{{ formalLevelText }}</b>
        </p>
        <p v-else-if="isPlacement && publishStatusText" class="mex-rpt-level mex-rpt-level-soft">
          {{ publishStatusText }}
          <template v-if="candidateLevelText"> · 候选 <b>{{ candidateLevelText }}</b></template>
          <template v-if="visitedLevelText"> · 触达 {{ visitedLevelText }}</template>
        </p>
        <p v-if="isSummative && promotionText" class="mex-rpt-promo">{{ promotionText }}</p>
        <p v-if="showPercentile" class="mex-rpt-peer">
          超过 <em>{{ report.nationalPercentile }}%</em> 同龄学员
        </p>
        <p v-else class="mex-rpt-peer mex-rpt-peer-muted">全国样本不足，暂不对标</p>
        <p v-if="report.title" class="mex-rpt-title">{{ report.title }} · {{ medalText }}</p>
      </div>
      <img class="mex-rpt-fox" :src="foxSrc" alt="">
    </div>

    <div class="mex-rpt-card">
      <h3 class="mex-rpt-h">
        <img class="mex-rpt-h-ico" :src="radarIconSrc" alt="">
        综合能力评估
      </h3>
      <div class="mex-rpt-radar-box" v-if="radarPlot">
        <ul class="mex-rpt-legend">
          <li><i class="mex-rpt-dot mex-rpt-dot-self"></i>本次测评</li>
          <li v-if="cityLine"><i class="mex-rpt-dot mex-rpt-dot-city"></i>{{ cityLegend }}</li>
          <li v-if="nationLine"><i class="mex-rpt-dot mex-rpt-dot-nation"></i>全国学生标准</li>
        </ul>
        <svg class="mex-rpt-svg" viewBox="0 0 420 360">
          <polygon v-for="(ring, i) in radarPlot.rings" :key="'r'+i" :points="ring" class="mex-rpt-ring"></polygon>
          <line v-for="(ax, i) in radarPlot.axes" :key="'a'+i" :x1="radarPlot.cx" :y1="radarPlot.cy" :x2="ax.x2" :y2="ax.y2" class="mex-rpt-axis"></line>
          <polygon v-if="radarPlot.nationPoly" :points="radarPlot.nationPoly" class="mex-rpt-poly-nation"></polygon>
          <polygon v-if="radarPlot.cityPoly" :points="radarPlot.cityPoly" class="mex-rpt-poly-city"></polygon>
          <polygon :points="radarPlot.selfPoly" class="mex-rpt-poly-self"></polygon>
          <circle v-for="(d, i) in radarPlot.selfDots" :key="'d'+i" :cx="d.x" :cy="d.y" r="4" class="mex-rpt-self-dot"></circle>
          <text v-for="(ax, i) in radarPlot.axes" :key="'l'+i" :x="ax.lx" :y="ax.ly" text-anchor="middle" class="mex-rpt-axis-lab">{{ ax.label }}</text>
        </svg>
      </div>
      <p v-else class="mex-rpt-hint">暂无雷达数据</p>
      <p class="mex-rpt-foot">{{ radarFoot }}</p>
    </div>

    <div v-if="isPlacement" class="mex-rpt-card">
      <h3 class="mex-rpt-h">
        <img class="mex-rpt-h-ico" :src="dimIconSrc" alt="">
        英语能力等级对照表
      </h3>
      <div class="mex-rpt-table-wrap">
        <table class="mex-rpt-table">
          <thead>
            <tr>
              <th></th>
              <th v-for="col in levelCols" :key="col.code" :class="{ 'is-on': col.code === levelText }">{{ col.code }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>美国 CCSS<br>对应年级</th>
              <td v-for="col in levelCols" :key="'c'+col.code" :class="{ 'is-on': col.code === levelText }">{{ col.ccss }}</td>
            </tr>
            <tr>
              <th>CEFR 欧洲<br>共同语言标准</th>
              <td v-for="col in levelCols" :key="'e'+col.code" :class="{ 'is-on': col.code === levelText }">{{ col.cefr }}</td>
            </tr>
            <tr>
              <th>剑桥标准</th>
              <td v-for="col in levelCols" :key="'m'+col.code" :class="{ 'is-on': col.code === levelText }">{{ col.cam }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="formalLevelText" class="mex-rpt-level-note">
        当前综合等级为 <b>{{ levelBrief }}</b>，继续加油，向更高的等级迈进！
      </p>
      <p v-else-if="isPlacement && publishStatusText" class="mex-rpt-level-note">
        {{ publishStatusText }}，非正式定级
        <template v-if="candidateLevelText">（候选 {{ candidateLevelText }}）</template>
        <template v-if="visitedLevelText">；最高触达 {{ visitedLevelText }}</template>。
      </p>
    </div>

    <div v-if="showDimAnalysis" class="mex-rpt-card">
      <h3 class="mex-rpt-h">
        <img class="mex-rpt-h-ico" :src="dimIconSrc" alt="">
        能力维度分析
      </h3>
      <div class="mex-rpt-tabs">
        <button v-for="tab in dimTabs" :key="tab.skill" type="button"
                class="mex-rpt-tab" :class="{ 'is-on': tab.skill === activeSkill }"
                @click="activeSkill = tab.skill">{{ tab.label }}</button>
      </div>
      <div v-if="activeDim" class="mex-rpt-dim">
        <div class="mex-rpt-dim-head">
          <div class="mex-rpt-dim-score">
            <span>{{ dimScoreInt }}</span><small>分</small>
          </div>
          <div v-if="activeDim.speakDims" class="mex-rpt-3d">
            <div><label>准确度</label><b>{{ num(activeDim.speakDims.accuracy) }}</b></div>
            <div><label>流利度</label><b>{{ num(activeDim.speakDims.fluency) }}</b></div>
            <div><label>完整度</label><b>{{ num(activeDim.speakDims.completeness) }}</b></div>
          </div>
          <div v-else-if="activeDim.subs && activeDim.subs.length" class="mex-rpt-3d">
            <div v-for="sub in activeDim.subs" :key="sub.code">
              <label>{{ sub.label }}</label>
              <b>{{ num(sub.score) }}</b>
            </div>
          </div>
        </div>
        <p v-if="activeDim.summary" class="mex-rpt-summary">{{ activeDim.summary }}</p>
        <div v-for="item in (activeDim.items || [])" :key="item.sessionItemId" class="mex-rpt-q">
          <div class="mex-rpt-q-top">
            <span class="mex-rpt-q-no">第 {{ item.seq }} 题</span>
            <span class="mex-rpt-q-diff">难度</span>
            <span class="mex-rpt-stars">{{ stars(item.difficultyLevel) }}</span>
          </div>
          <div v-if="item.audioUrl" class="mex-rpt-audio-block">
            <p>宝贝发音</p>
            <audio :src="item.audioUrl" controls class="mex-rpt-audio"></audio>
          </div>
          <div class="mex-rpt-q-actions">
            <button v-if="item.explanation" type="button" class="mex-rpt-btn-fill"
                    @click="toggleExplain(item.sessionItemId)">查看解析</button>
            <button v-if="sampleUrl(item)" type="button" class="mex-rpt-btn-ghost"
                    @click="toggleSample(item.sessionItemId)">示范音</button>
          </div>
          <p v-if="openedExplain[item.sessionItemId]" class="mex-rpt-explain">{{ item.explanation }}</p>
          <audio v-if="openedSample[item.sessionItemId] && sampleUrl(item)"
                 :src="sampleUrl(item)" controls class="mex-rpt-audio"></audio>
        </div>
        <p v-if="!(activeDim.items && activeDim.items.length)" class="mex-rpt-hint">该维暂无逐题</p>
        <p v-if="activeDim.tip" class="mex-rpt-tip">小贴士：{{ activeDim.tip }}</p>
      </div>
    </div>

    <div v-if="report.advice" class="mex-rpt-card">
      <h3 class="mex-rpt-h">学习建议</h3>
      <p class="mex-rpt-advice">{{ report.advice }}</p>
    </div>
  </div>
</template>

<script>
import { assetUrl } from './mex-asset'

export default {
  name: 'MexSessionReport',
  props: {
    report: { type: Object, default: null }
  },
  data () {
    return {
      foxSrc: require('./report-assets/fox.png'),
      radarIconSrc: require('./report-assets/icon-radar.svg'),
      dimIconSrc: require('./report-assets/icon-dim.svg'),
      activeSkill: 'Speaking',
      openedExplain: {},
      openedSample: {},
      levelCols: [
        { code: 'L1', ccss: 'ESL Skill Building', cefr: 'Pre-A1', cam: 'YLE-Starters' },
        { code: 'L2', ccss: 'Grade K', cefr: 'Pre-A1', cam: 'YLE-Starters' },
        { code: 'L3', ccss: 'Grade K', cefr: 'Pre-A1', cam: 'YLE-Starters' },
        { code: 'L4', ccss: 'Grade 1', cefr: 'A1', cam: 'YLE Movers' },
        { code: 'L5', ccss: 'Grade 2', cefr: 'A1', cam: 'YLE Movers' },
        { code: 'L6', ccss: 'Grade 3', cefr: 'A2', cam: 'YLE Flyers' },
        { code: 'L7', ccss: 'Grade 4', cefr: 'A2-B1', cam: 'Flyers / KET' },
        { code: 'L8', ccss: 'Grade 5', cefr: 'B1', cam: 'PET' },
        { code: 'L9', ccss: 'Grade 6', cefr: 'B2', cam: 'PET' }
      ]
    }
  },
  watch: {
    report () {
      this.openedExplain = {}
      this.openedSample = {}
      this.activeSkill = 'Speaking'
    }
  },
  computed: {
    isPlacement () {
      return this.report && this.report.examType === 'PLACEMENT'
    },
    isSummative () {
      return this.report && this.report.examType === 'SUMMATIVE'
    },
    showDimAnalysis () {
      if (!this.report) return false
      if (this.isPlacement || this.isSummative) return true
      let dims = this.report.dimensions
      return !!(dims && dims.length)
    },
    kicker () {
      return this.isPlacement ? '英语能力测评报告' : '成绩报告'
    },
    scoreInt () {
      let n = Number(this.report && this.report.totalScore)
      if (isNaN(n)) return '—'
      return Math.round(n)
    },
    levelText () {
      // 对照表高亮：正式用 confirmed；暂定用 candidate；否则 placementLevel
      let raw = this.formalLevelRaw || this.candidateLevelRaw || (this.report && this.report.placementLevel)
      if (!raw) return ''
      return String(raw).replace(/\.0$/, '')
    },
    formalLevelRaw () {
      if (!this.report || this.report.examType !== 'PLACEMENT') return ''
      let st = this.report.publishStatus
      if (st && st !== 'VERIFIED') return ''
      return this.report.confirmedLevel || this.report.placementLevel || ''
    },
    formalLevelText () {
      let raw = this.formalLevelRaw
      if (!raw) return ''
      return String(raw).replace(/\.0$/, '')
    },
    candidateLevelRaw () {
      return (this.report && this.report.candidateLevel) || ''
    },
    candidateLevelText () {
      let raw = this.candidateLevelRaw
      if (!raw) return ''
      return String(raw).replace(/\.0$/, '')
    },
    visitedLevelText () {
      let raw = this.report && this.report.visitedMaxLevel
      if (!raw) return ''
      return String(raw).replace(/\.0$/, '')
    },
    publishStatusText () {
      let st = this.report && this.report.publishStatus
      let m = {
        PROVISIONAL: '暂定等级（非正式）',
        REVIEW: '待复核（无正式定级）',
        BELOW_L1: '未达 L1（无正式定级）'
      }
      return m[st] || ''
    },
    levelBrief () {
      let code = this.formalLevelText || this.levelText
      let col = this.levelCols.filter(c => c.code === code)[0]
      if (!col) return code
      return code + '（' + col.ccss + ' / ' + col.cefr + ' / ' + col.cam + '）'
    },
    showPercentile () {
      return this.report && this.report.benchmarkVisible === true && this.report.nationalPercentile != null
    },
    medalText () {
      let m = {
        GOLD: '金松果',
        SILVER: '银松果',
        BRONZE: '铜松果',
        IRON: '铁松果',
        WOOD: '木松果',
        GRASS: '草编松果'
      }
      return m[this.report && this.report.medal] || (this.report && this.report.medal) || ''
    },
    promotionText () {
      let m = {
        UP: '升班建议：升班',
        CONSOLIDATE: '升班建议：巩固',
        REMEDIATE: '升班建议：补弱',
        REPEAT: '升班建议：重学'
      }
      return m[this.report && this.report.promotion] || ''
    },
    cityLine () {
      return this.parseObj(this.report && this.report.cityRadarJson)
    },
    nationLine () {
      return this.parseObj(this.report && this.report.nationalRadarJson)
    },
    cityLegend () {
      let name = (this.report && this.report.cityName) || ''
      return name ? name + '学生标准' : '同城学生标准'
    },
    radarFoot () {
      return this.isPlacement
        ? '五项总得分均为100分，图中数据为各维度得分。'
        : '六项总得分均为100分，图中数据为各维度得分。'
    },
    radarKeys () {
      if (this.isPlacement) {
        return [
          ['listen', '听力理解'],
          ['speak', '口语表达'],
          ['read', '阅读理解'],
          ['vocab', '词汇运用'],
          ['grammar', '语法运用']
        ]
      }
      return [
        ['listen', '听力理解'],
        ['speak', '口语表达'],
        ['read', '阅读理解'],
        ['vocab', '词汇运用'],
        ['grammar', '语法运用'],
        ['write', '写作运用']
      ]
    },
    radarPlot () {
      let self = this.parseObj(this.report && this.report.radarJson)
      let keys = this.radarKeys
      if (!keys.length) return null
      let has = false
      for (let i = 0; i < keys.length; i++) {
        if (self[keys[i][0]] != null) has = true
      }
      if (!has) return null
      let city = this.cityLine
      let nation = this.nationLine
      let cx = 210
      let cy = 180
      let r = 108
      let n = keys.length
      let pt = (i, ratio) => {
        let ang = -Math.PI / 2 + i * 2 * Math.PI / n
        return {
          x: +(cx + r * ratio * Math.cos(ang)).toFixed(1),
          y: +(cy + r * ratio * Math.sin(ang)).toFixed(1)
        }
      }
      let rings = []
      let levels = [0.25, 0.5, 0.75, 1]
      for (let L = 0; L < levels.length; L++) {
        let pts = []
        for (let i = 0; i < n; i++) {
          let p = pt(i, levels[L])
          pts.push(p.x + ',' + p.y)
        }
        rings.push(pts.join(' '))
      }
      let axes = []
      let selfDots = []
      let selfPoly = []
      let cityPoly = []
      let nationPoly = []
      let hasCity = false
      let hasNation = false
      for (let i = 0; i < n; i++) {
        let outer = pt(i, 1)
        let lab = pt(i, 1.28)
        let score = Number(self[keys[i][0]])
        if (isNaN(score)) score = 0
        let d = pt(i, Math.max(0, Math.min(100, score)) / 100)
        selfPoly.push(d.x + ',' + d.y)
        selfDots.push(d)
        axes.push({ x2: outer.x, y2: outer.y, lx: lab.x, ly: lab.y, label: keys[i][1] })
        if (city && city[keys[i][0]] != null) {
          hasCity = true
          let cs = Number(city[keys[i][0]])
          if (isNaN(cs)) cs = 0
          let cp = pt(i, Math.max(0, Math.min(100, cs)) / 100)
          cityPoly.push(cp.x + ',' + cp.y)
        }
        if (nation && nation[keys[i][0]] != null) {
          hasNation = true
          let ns = Number(nation[keys[i][0]])
          if (isNaN(ns)) ns = 0
          let np = pt(i, Math.max(0, Math.min(100, ns)) / 100)
          nationPoly.push(np.x + ',' + np.y)
        }
      }
      return {
        cx: cx,
        cy: cy,
        rings: rings,
        axes: axes,
        selfDots: selfDots,
        selfPoly: selfPoly.join(' '),
        cityPoly: hasCity ? cityPoly.join(' ') : '',
        nationPoly: hasNation ? nationPoly.join(' ') : ''
      }
    },
    dimTabs () {
      let tabs = [
        { skill: 'Listening', label: '听力' },
        { skill: 'Speaking', label: '口语' },
        { skill: 'Reading', label: '阅读' },
        { skill: 'Vocab', label: '词汇' },
        { skill: 'Grammar', label: '语法' }
      ]
      if (this.isSummative) {
        tabs.push({ skill: 'Writing', label: '写作' })
      }
      return tabs
    },
    activeDim () {
      let dims = (this.report && this.report.dimensions) || []
      for (let i = 0; i < dims.length; i++) {
        if (dims[i].skill === this.activeSkill) return dims[i]
      }
      return dims[0] || null
    },
    dimScoreInt () {
      let n = Number(this.activeDim && this.activeDim.score)
      if (isNaN(n)) return '—'
      return Math.round(n)
    }
  },
  methods: {
    parseObj (raw) {
      if (!raw) return null
      if (typeof raw === 'object') return raw
      try {
        return JSON.parse(raw)
      } catch (e) {
        return null
      }
    },
    num (v) {
      if (v === undefined || v === null || v === '') return '—'
      return v
    },
    stars (n) {
      let x = Number(n)
      if (isNaN(x) || x < 1) x = 0
      if (x > 5) x = 5
      let s = ''
      for (let i = 1; i <= 5; i++) s += i <= x ? '★' : '☆'
      return s
    },
    sampleUrl (item) {
      if (!item || !item.sampleAudio) return ''
      let skill = this.activeSkill === 'Vocab' || this.activeSkill === 'Grammar' ? 'Reading' : this.activeSkill
      return assetUrl(item.sampleAudio, skill)
    },
    toggleExplain (id) {
      this.$set(this.openedExplain, id, !this.openedExplain[id])
    },
    toggleSample (id) {
      this.$set(this.openedSample, id, !this.openedSample[id])
    }
  }
}
</script>

<style scoped>
.mex-rpt {
  font-family: 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  color: #222;
  background: #fef6ea;
  margin: -16px;
  padding: 0 0 24px;
}
.mex-rpt-hero {
  position: relative;
  min-height: 220px;
  padding: 28px 36px 20px;
  background: linear-gradient(180deg, #fca039 0%, #ff8a2b 100%);
  overflow: hidden;
  color: #3c1d01;
}
.mex-rpt-kicker {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 600;
}
.mex-rpt-score-row {
  display: flex;
  align-items: baseline;
  color: #ff6b09;
  text-shadow: 0 1px 0 rgba(255,255,255,.35);
}
.mex-rpt-score {
  font-size: 88px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -2px;
}
.mex-rpt-score-unit {
  margin-left: 6px;
  font-size: 28px;
  font-weight: 600;
}
.mex-rpt-level,
.mex-rpt-promo,
.mex-rpt-title {
  margin: 8px 0 0;
  font-size: 16px;
}
.mex-rpt-level b {
  font-size: 28px;
}
.mex-rpt-peer {
  margin: 10px 0 0;
  font-size: 18px;
  font-weight: 600;
}
.mex-rpt-peer em {
  color: #ff6b09;
  font-style: normal;
  font-size: 22px;
}
.mex-rpt-peer-muted {
  color: #7d2d13;
  font-weight: 400;
  font-size: 14px;
}
.mex-rpt-fox {
  position: absolute;
  right: 12px;
  bottom: 0;
  height: 210px;
  width: auto;
  pointer-events: none;
}
.mex-rpt-card {
  margin: 16px 20px 0;
  padding: 20px 22px 16px;
  background: #fff;
  border: 1px solid #ffe5c9;
  border-radius: 20px;
  box-shadow: 0 4px 6px 2px rgba(254, 125, 33, 0.18);
}
.mex-rpt-h {
  display: flex;
  align-items: center;
  margin: 0 0 12px;
  color: #7d2d13;
  font-size: 20px;
  font-weight: 600;
}
.mex-rpt-h-ico {
  width: 28px;
  height: 28px;
  margin-right: 8px;
  object-fit: contain;
}
.mex-rpt-legend {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
  color: #545454;
  font-size: 13px;
}
.mex-rpt-legend li {
  margin-bottom: 4px;
}
.mex-rpt-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 8px;
  border-radius: 2px;
  vertical-align: middle;
}
.mex-rpt-dot-self { background: #ff8a2b; }
.mex-rpt-dot-city { background: #4a90d9; }
.mex-rpt-dot-nation { background: #21cf75; }
.mex-rpt-radar-box {
  display: flex;
  align-items: center;
}
.mex-rpt-svg {
  width: 420px;
  height: 360px;
  flex-shrink: 0;
}
.mex-rpt-ring {
  fill: none;
  stroke: #f0d9b8;
  stroke-width: 1;
}
.mex-rpt-axis {
  stroke: #f0d9b8;
  stroke-width: 1;
}
.mex-rpt-poly-self {
  fill: rgba(255, 138, 43, 0.28);
  stroke: #ff8a2b;
  stroke-width: 2;
}
.mex-rpt-poly-city {
  fill: none;
  stroke: #4a90d9;
  stroke-width: 2;
  stroke-dasharray: 6 4;
}
.mex-rpt-poly-nation {
  fill: none;
  stroke: #21cf75;
  stroke-width: 2;
  stroke-dasharray: 6 4;
}
.mex-rpt-self-dot { fill: #ff8a2b; }
.mex-rpt-axis-lab {
  fill: #545454;
  font-size: 13px;
  font-weight: 600;
}
.mex-rpt-foot,
.mex-rpt-hint {
  margin: 8px 0 0;
  color: #545454;
  font-size: 13px;
}
.mex-rpt-table-wrap { overflow-x: auto; }
.mex-rpt-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  text-align: center;
}
.mex-rpt-table th,
.mex-rpt-table td {
  border: 1px solid #ffe0c2;
  padding: 8px 4px;
  color: #272727;
}
.mex-rpt-table thead th {
  background: linear-gradient(180deg, #ff8833, #ff6a1d);
  color: #fff;
  font-size: 14px;
}
.mex-rpt-table thead th:first-child,
.mex-rpt-table tbody th {
  background: #fff8ee;
  color: #222;
  width: 110px;
  font-weight: 600;
}
.mex-rpt-table .is-on {
  background: #fff3d6;
  font-weight: 700;
  color: #ff6510;
}
.mex-rpt-level-note {
  margin: 12px 0 0;
  padding: 12px 16px;
  background: #fef8ee;
  border: 1px solid #ffead1;
  border-radius: 12px;
  font-size: 14px;
}
.mex-rpt-level-note b { color: #ff6510; }
.mex-rpt-tabs {
  display: flex;
  background: #fff9ef;
  border-radius: 16px;
  padding: 6px;
  margin-bottom: 16px;
}
.mex-rpt-tab {
  flex: 1;
  border: 0;
  background: transparent;
  height: 40px;
  border-radius: 12px;
  color: #333;
  font-size: 15px;
  cursor: pointer;
}
.mex-rpt-tab.is-on {
  background: #ff8a2b;
  color: #fff;
  font-weight: 600;
  box-shadow: 0 2px 3px rgba(255, 100, 28, 0.25);
}
.mex-rpt-dim-head {
  display: flex;
  align-items: center;
  gap: 24px;
}
.mex-rpt-dim-score {
  color: #ff650f;
  font-weight: 700;
}
.mex-rpt-dim-score span { font-size: 64px; line-height: 1; }
.mex-rpt-dim-score small { font-size: 22px; margin-left: 4px; }
.mex-rpt-3d {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  justify-content: space-around;
  text-align: center;
  gap: 8px 12px;
}
.mex-rpt-3d label {
  display: block;
  color: #545454;
  font-size: 14px;
}
.mex-rpt-3d b {
  color: #21cf75;
  font-size: 36px;
  line-height: 1.2;
}
.mex-rpt-summary {
  margin: 12px 0;
  padding: 12px 14px;
  background: #f7f7f0;
  border-radius: 12px;
  color: #545454;
  line-height: 1.6;
}
.mex-rpt-q {
  padding: 16px 0;
  border-top: 1px dashed #f0d9b8;
}
.mex-rpt-q-top { margin-bottom: 8px; }
.mex-rpt-q-no {
  color: #ff650f;
  font-size: 18px;
  font-weight: 600;
  margin-right: 16px;
}
.mex-rpt-q-diff { color: #333; margin-right: 8px; }
.mex-rpt-stars { color: #ffb020; letter-spacing: 2px; }
.mex-rpt-audio-block p { margin: 0 0 6px; color: #333; }
.mex-rpt-audio { width: 100%; max-width: 480px; }
.mex-rpt-q-actions { margin-top: 10px; }
.mex-rpt-btn-fill,
.mex-rpt-btn-ghost {
  height: 36px;
  padding: 0 18px;
  margin-right: 12px;
  border-radius: 18px;
  font-size: 14px;
  cursor: pointer;
}
.mex-rpt-btn-fill {
  border: 0;
  background: #ff8a2b;
  color: #fff;
}
.mex-rpt-btn-ghost {
  border: 1px solid #ff8a2b;
  background: #fff;
  color: #ff6510;
}
.mex-rpt-explain,
.mex-rpt-advice {
  margin: 10px 0 0;
  line-height: 1.7;
  color: #333;
  white-space: pre-wrap;
}
.mex-rpt-tip {
  margin: 12px 0 0;
  padding: 12px 16px;
  background: #fff4e8;
  border-radius: 12px;
  color: #333;
}
</style>



// WEBPACK FOOTER //
// MexSessionReport.vue?12ba26c0